import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import BrandKeylineBackground from '@/components/atoms/BrandKeylineBackground/BrandKeylineBackground';
import Button from '@/components/atoms/Button/Button';
import ButtonIcon from '@/components/atoms/ButtonIcon/ButtonIcon';
import EmphasisedText from '@/components/atoms/EmphasisedText/EmphasisedText';
import SectionWrapper from '@/components/atoms/SectionWrapper/SectionWrapper';
import Typography from '@/components/atoms/Typography/Typography';
import CardOffer from '@/components/molecules/CardOffer/CardOffer';
import Carousel from '@/components/molecules/Carousel/Carousel';
import { cn } from '@/helpers/cn';
import { dataLayerPush } from '@/helpers/dataLayer';
import { guaranteeArrayMinLength } from '@/helpers/guaranteeArrayMinLength';
import { useIsClient } from '@/hooks/useIsClient';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { applyBottomPadding, applyTopPadding } from '@/utils/themeUtils';
import { motion, useMotionValueEvent, useScroll, useSpring, useTransform } from 'motion/react';
import React, { useRef, useState } from 'react';
import { EffectCoverflow } from 'swiper/modules';
import { useContainerMinHeight, useIsAndroid, useIsIOS, useShouldAnimate } from './OfferListingHero.hooks';
const OfferListingHero = ({ centreTheme, componentID, title, subTitle, theme, items, link, topPadding, bottomPadding, nextIcon = 'chevron-right', nextLabel = 'Next', previousIcon = 'chevron-left', previousLabel = 'Previous', slideProgressLabel = 'Slide {0} of {1}', anchorNavTitle, saveLabel, removeLabel, cookieName, saveIcon, activeSaveIcon, saveItemEndpoint, isInEditor, reactComponentName, }) => {
    const isIOS = useIsIOS();
    const isAndroid = useIsAndroid();
    const isMobile = isIOS || isAndroid;
    const isClient = useIsClient();
    const shouldAnimate = useShouldAnimate();
    const prefersReducedMotion = usePrefersReducedMotion();
    const containerMinHeight = useContainerMinHeight();
    const containerRef = useRef(null);
    const scroll = useScroll({
        target: containerRef,
        offset: ['start start', 'end end'],
    });
    const isOfferListingHero = reactComponentName && reactComponentName.includes('OfferListingHero');
    const ANIMATION_CONFIG = { stiffness: 120, damping: 30, restDelta: 0.0005 };
    const FINAL_TOP_POSITION = isOfferListingHero ? 0 : 5;
    const COMPONENT_NAME = isOfferListingHero ? 'Offer Listing Hero' : 'Offer Listing';
    const scrollYProgress = useSpring(scroll.scrollYProgress, ANIMATION_CONFIG);
    const carouselOpacity = useTransform(scrollYProgress, [0.45, 0.5, 0.55], [0, 0.7, 1]);
    const carouselY = useTransform(scrollYProgress, [0.45, 0.7, 1], [-20, FINAL_TOP_POSITION - 20, FINAL_TOP_POSITION]);
    const listOpacity = useTransform(scrollYProgress, [0.5, 0.6], [1, 0]);
    const [isAtEnd, setIsAtEnd] = useState(false);
    const [startAnimation, setStartAnimation] = useState(false);
    useMotionValueEvent(scrollYProgress, 'change', (value) => {
        if (value >= 0.01)
            setStartAnimation(true);
        if (value >= 0.7)
            setIsAtEnd(true);
    });
    const MAX_ITEMS_CAROUSEL = 5;
    const cards = React.useMemo(() => {
        if (items.length === 0) {
            return [];
        }
        // Duplicate the array until we have the maximum number of items that can display.
        // This is to try and align the "preview" with the actual carousel after the animation.
        const duplicateItems = guaranteeArrayMinLength(items, 5);
        // Attempt to replicate the carousel order.
        // Uses a centered carousel layout which loops so the centre item should be the first one
        // and loop around. E.g. [3, 4, 0, 1, 2]
        return [
            duplicateItems.at(-2),
            duplicateItems.at(-1),
            duplicateItems.at(0),
            duplicateItems.at(1),
            duplicateItems.at(2),
        ];
    }, [items]);
    const onCtaClick = (text, url) => {
        dataLayerPush('ctaInteraction', {
            cta_type: COMPONENT_NAME,
            click_text: text,
            click_url: url,
        });
    };
    const carousel = (_jsx(Carousel, { items: items, minimumSlides: 6, onSlideClassName: () => '!w-[70%] sm:!w-[30%] md:!w-[19%] !h-auto mx-2  md:mx-0', className: "!flex items-center justify-center md:min-h-[450px] xl:min-h-[700px]", onSlideRender: ({ item, index, swiperRef, accessibilityProps }) => {
            if (!swiperRef)
                return null;
            const MOBILE_BREAKPOINT = 700;
            const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
            const slideElements = isMobile
                ? Array.from(swiperRef.slides)
                : Array.from(swiperRef.slides).filter((el) => el.classList.contains('swiper-slide-fully-visible'));
            // Find current slide and its position
            const currentSlide = slideElements.find((el) => el.swiperSlideIndex === index);
            const positionIndex = slideElements.findIndex((el) => el.swiperSlideIndex === index);
            // Determine slide position type
            const isCenter = currentSlide?.classList.contains('swiper-slide-active');
            const isPrev = currentSlide?.classList.contains('swiper-slide-prev');
            const isNext = currentSlide?.classList.contains('swiper-slide-next');
            const isFirst = positionIndex === 0;
            const isLast = positionIndex === slideElements.length - 1;
            const direction = swiperRef.swipeDirection;
            let transformClasses;
            if (!currentSlide) {
                transformClasses = ' rotate-0 md:translate-y-[115px]';
            }
            else if (isCenter) {
                transformClasses = 'rotate-0 z-10 ';
            }
            else if (isPrev) {
                transformClasses = 'translate-y-[15px] rotate-[-6deg]  z-[5]';
            }
            else if (isNext) {
                transformClasses = 'translate-y-[15px] rotate-[6deg]  z-[5]';
            }
            else if (isFirst) {
                transformClasses = 'md:translate-y-[80px] md:rotate-[-12deg]';
            }
            else if (isLast) {
                transformClasses = 'md:translate-y-[80px] md:rotate-[12deg]';
            }
            else if (direction === 'next' && !isMobile) {
                transformClasses = 'md:translate-y-[115px] rotate-[6deg]';
            }
            else {
                transformClasses = 'md:translate-y-[115px] rotate-[-6deg]';
            }
            return (_jsx("div", { className: cn('h-full scale-95 transition-all duration-500 ease-out will-change-transform', transformClasses), "data-show-after-hydration": true, children: _jsx(CardOffer, { ...item, additionalDataLayerProps: { card_type: COMPONENT_NAME }, className: "h-full", headingLevel: "h2", accessibilityProps: accessibilityProps, saveLabel: saveLabel, removeLabel: removeLabel, cookieName: cookieName, saveIcon: saveIcon, activeSaveIcon: activeSaveIcon, saveItemEndpoint: saveItemEndpoint, parentComponent: "Offer Listing Hero" }) }));
        }, onSlideImpressions: (items) => {
            // TODO: This component gets the Offer data model from another component props.
            // This should be moved to an OfferModel in global.d.ts to avoid interlinking.
            // Casting to what the data model actually is for now.
            const typedItems = items;
            dataLayerPush('featuredBrand', {
                list_interaction: 'Brand Impression',
                brand_list: 'Listing Hero',
                brand: {
                    impressions: typedItems.map(({ item, index }) => ({
                        brand_name: item.brand,
                        position: index + 1,
                    })),
                },
            });
        }, hideSlidesUntilHydrated: !isMobile, options: {
            centeredSlides: true,
            slidesPerView: 'auto',
            effect: !isMobile ? 'coverflow' : undefined,
            loop: true,
            grabCursor: false,
            speed: 700,
            onResize: (swiperRef) => {
                swiperRef.update();
                swiperRef.slideTo(0);
            },
            ...(isMobile
                ? {}
                : {
                    coverflowEffect: {
                        rotate: 0,
                        slideShadows: false,
                        stretch: -25,
                        modifier: 4.5,
                    },
                }),
            modules: isMobile ? [] : [EffectCoverflow],
            onTouchMove: (swiperRef) => {
                if (!isMobile) {
                    swiperRef.update();
                }
            },
            breakpoints: {
                700: {
                    coverflowEffect: {
                        rotate: 0,
                        depth: 0,
                        slideShadows: false,
                        stretch: -25,
                        modifier: 6,
                    },
                    speed: 800,
                },
                1024: {
                    coverflowEffect: {
                        rotate: 0,
                        depth: 0,
                        slideShadows: false,
                        stretch: -15,
                        modifier: 5,
                    },
                    speed: 600,
                    slidesPerView: 'auto',
                },
            },
        }, labels: {
            previousLabel,
            nextLabel,
            previousIcon,
            nextIcon,
            slideProgressLabel,
        }, onAdditionalDataLayerProps: () => ({ carousel_type: COMPONENT_NAME }), children: ({ slides, pagination, previousButton, nextButton }) => (_jsxs("div", { className: "relative flex flex-col gap-1.5 lg:gap-0.75 sm:[&_.swiper-wrapper]:!pb-[6vw] xl:[&_.swiper-wrapper]:!pb-0 min-[1500px]:[&_.swiper-wrapper]:!pb-[2vw] min-[1700px]:[&_.swiper-wrapper]:!pb-[4vw]", children: [slides, _jsxs("div", { className: "mx-site-padding grid grid-cols-12 pt-1 md:pt-2", children: [_jsx("div", { className: "col-span-6 md:col-span-4", children: pagination }), _jsx("div", { className: "col-span-4 hidden items-center justify-center md:flex", children: link && (_jsx("div", { "data-testid": "offer-listing-hero-button", children: _jsx(Button, { mode: "interactive", variant: link.linkDisplay, children: (buttonProps) => (_jsxs("a", { href: link.url, target: link.target, "aria-label": link.ariaLabel, ...buttonProps, onClick: () => onCtaClick(link.text, link.url), children: [_jsx("span", { children: link.text }), link.icon && _jsx(ButtonIcon, { variant: link.icon })] })) }) })) }), _jsxs("div", { className: "col-span-6 flex flex-1 justify-end gap-0.5 md:col-span-4", children: [_jsx("div", { children: previousButton }), _jsx("div", { children: nextButton })] })] }), link && (_jsx("div", { className: "mx-site-padding flex items-center md:hidden", "data-testid": "offer-listing-hero-button-desktop", children: _jsx(Button, { mode: "interactive", variant: link.linkDisplay, children: (buttonProps) => (_jsxs("a", { href: link.url, target: link.target, "aria-label": link.ariaLabel, ...buttonProps, className: cn(buttonProps.className, 'w-full'), onClick: () => onCtaClick(link.text, link.url), children: [_jsx("span", { children: link.text }), link.icon && _jsx(ButtonIcon, { variant: link.icon })] })) }) }))] })) }));
    return (_jsx(SectionWrapper, { "data-test-id": "OfferListingHero-organism", "data-epi-block-id": componentID, width: "full", centreTheme: centreTheme, theme: theme, className: "relative overflow-clip light-a:bg-surface-secondary dark-a:bg-surface-expressive", anchorNavTitle: anchorNavTitle, children: _jsx("div", { className: cn(shouldAnimate && 'min-h-[150vh]', shouldAnimate && isInEditor && 'max-h-[600px] min-h-0', 'flex flex-col overflow-clip transition-opacity duration-1250'), ref: containerRef, children: _jsxs(motion.div, { className: cn(shouldAnimate && 'sticky', shouldAnimate && containerMinHeight.initial, shouldAnimate && isInEditor && 'max-h-[600px] min-h-0', 'top-0 flex w-full flex-col justify-center gap-1.5 md:gap-2.5', applyTopPadding('sm'), applyBottomPadding('sm')), style: isInEditor ? undefined : containerMinHeight.animations, ref: containerMinHeight.ref, children: [!prefersReducedMotion && (_jsx(BrandKeylineBackground, { className: "absolute top-1/2 z-0 aspect-square w-full -translate-y-1/2 scale-140", animationEnabled: startAnimation, overflow: true, duration: 3000 })), _jsxs("div", { className: "relative z-10 flex flex-col gap-1.5 px-site-padding light-a:text-text-headers dark-a:text-text-headers-expressive", children: [title && (_jsx(Typography, { variant: "heading-2xl", as: isOfferListingHero ? 'h1' : 'h2', className: "mx-auto text-center", "data-testid": "title", children: _jsx(EmphasisedText, { children: title }) })), subTitle && (_jsx(Typography, { as: "span", variant: "subtitle-md", className: "mx-auto text-center text-[26px]", "data-testid": "subtitle", children: subTitle }))] }), _jsxs("div", { className: "relative", children: [_jsx(motion.div, { className: cn('absolute top-16 left-0 flex w-full items-center justify-center', isAtEnd && 'hidden'), style: {
                                    scale: useTransform(scrollYProgress, [0.4, 0.5], [0.95, 1]),
                                    opacity: shouldAnimate ? listOpacity : 0,
                                }, "aria-hidden": shouldAnimate ? undefined : true, inert: shouldAnimate ? undefined : '', children: cards.map((card, index, arr) => {
                                    // Calculate base values relative to center position (index 2)
                                    const positionFromCenter = index - 2;
                                    const isFirst = index === 0;
                                    const isLast = index === arr.length - 1;
                                    const offsetY = isFirst || isLast ? useTransform(scrollYProgress, [0, 0.3], [85, 85]) : 0;
                                    const baseX = positionFromCenter * 30;
                                    const baseRotate = positionFromCenter * 4;
                                    const offsetX = useTransform(scrollYProgress, [0, 0.3], [baseX, positionFromCenter * 400]);
                                    const rotate = useTransform(scrollYProgress, [0, 0.4], [baseRotate, baseRotate * 2]);
                                    const baseOpacity = 1 - Math.abs(positionFromCenter) * 0.15;
                                    const opacity = useTransform(scrollYProgress, [0, 0.4], [baseOpacity, 0.1]);
                                    const zIndex = MAX_ITEMS_CAROUSEL - Math.abs(positionFromCenter);
                                    return (_jsx(motion.div, { style: {
                                            translateX: offsetX,
                                            translateY: offsetY,
                                            rotate,
                                            opacity,
                                            zIndex,
                                        }, className: "absolute top-1/2 left-1/2 w-[270px] -translate-x-1/2 -translate-y-1/2 scale-90 md:w-[300px] lg:w-[350px]", children: _jsx(CardOffer, { ...card, additionalDataLayerProps: { card_type: COMPONENT_NAME }, headingLevel: "h2", saveLabel: saveLabel, removeLabel: removeLabel, cookieName: cookieName, saveIcon: saveIcon, activeSaveIcon: activeSaveIcon, saveItemEndpoint: saveItemEndpoint, parentComponent: "Offer Listing Hero" }) }, `card-${index}-${card.title}`));
                                }) }), _jsx(motion.div, { style: {
                                    opacity: isAtEnd || !shouldAnimate ? 1 : carouselOpacity,
                                    y: carouselY,
                                }, className: "flex w-full items-center justify-center xl:-mt-[5vw] min-[1600px]:-mt-[4vw] min-[1800px]:-mt-[1vw]", children: _jsxs("div", { className: "w-full", children: [isClient && carousel, !isClient && _jsx("div", { className: "min-h-screen" })] }) })] })] }) }) }));
};
export default OfferListingHero;
